/**
 * NotFoundPage — the page that is not there. The same card as every wait and empty state
 * (components/GlassOrb.tsx LoadingCard): the kitten takes the blame, the buttons do the work.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';
import { BrandMark } from '../components/BrandMark';
import { SAYINGS } from '../utils/sayings';

export const NotFoundPage: React.FC = () => {
  usePageMeta(PAGE_META.notFound);
  const location = useLocation();
  const saying = SAYINGS.oops;

  return (
    <div className="relative flex flex-col min-h-screen bg-cream ns-dotgrid">
      <main className="relative flex-1 flex items-center justify-center px-4 py-10">
        <div className="ns-loading-card ns-glass ns-glass-strong animate-fade-in-up" role="main" aria-labelledby="not-found-title">
          <img src={saying.picture} alt={saying.alt} className="ns-loading-card__picture" decoding="async" draggable={false} />
          <h1 id="not-found-title" className="ns-loading-card__line">{saying.line}</h1>
          <p className="ns-t-body text-ink-soft px-1">
            There is no page at <code className="ns-t-mono text-ink break-all">{location.pathname}</code>. The canvas is this way.
          </p>
          <div className="flex flex-wrap items-center gap-2 px-1 pb-1">
            <Link to="/" className="ns-btn ns-btn-primary ns-btn-sm">
              <BrandMark size={16} tone="onDark" />
              Back to your library
            </Link>
            <button type="button" onClick={() => window.history.back()} className="ns-btn ns-btn-ghost ns-btn-sm">
              <ArrowLeft size={16} aria-hidden="true" />
              Go back
            </button>
          </div>
        </div>
      </main>

      <footer className="relative py-3 px-4 sm:px-6 border-t border-cream-300 bg-cream/70 backdrop-blur-sm shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <p className="text-ink-soft text-xs font-medium">© 2026 Noodle Studio</p>
          <nav aria-label="Footer" className="flex items-center gap-1">
            <Link to="/about" className="ns-btn ns-btn-quiet ns-btn-xs">About</Link>
            <Link to="/terms" className="ns-btn ns-btn-quiet ns-btn-xs">Terms</Link>
            <Link to="/privacy" className="ns-btn ns-btn-quiet ns-btn-xs">Privacy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default NotFoundPage;
